import React, { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { gsap } from 'gsap';
import { Draggable } from 'gsap/dist/Draggable';

import { InertiaPlugin } from '@pkg/assets/js/gsap-plugins/InertiaPlugin';
import { YatScrim } from '@pkg/components';
import { DepButton } from '@pkg/components/deprecated';

import { useLockBodyScroll, useWindowResize } from '@app/helpers';

import './ModalBottomSheet.scss';

gsap.registerPlugin(Draggable, InertiaPlugin);

interface YatModalBottomSheetProps {
    children: ReactNode;
    modalLabelId: string;
    open: boolean;
    closeCallback: () => void;
    backdrop?: boolean;
    scrollable?: boolean;
    variant?: 'dark' | 'light';
    classes?: string;
}

export const YatModalBottomSheet = ({
    children,
    modalLabelId,
    open,
    closeCallback,
    backdrop = true,
    scrollable = true,
    variant = 'dark',
    classes,
}: YatModalBottomSheetProps) => {
    const containerElement = useRef() as React.MutableRefObject<HTMLDivElement>;
    const sheetElement = useRef() as React.MutableRefObject<HTMLDivElement>;
    const handleElement = useRef() as React.MutableRefObject<HTMLDivElement>;
    const [focusableChilds, setFocusableChilds] = useState<NodeListOf<Element>>();
    const [focusedElementBeforeOpen, setFocusedElementBeforeOpen] = useState<HTMLElement>();
    const [isDragging, setIsDragging] = useState(false);
    const [snap, setSnap] = useState({ top: 0, bottom: 0, threshold: 0 });
    const windowSize = useWindowResize();
    useLockBodyScroll(open);

    const getSizes = useCallback(() => {
        if (sheetElement.current) {
            setSnap({
                top: 0,
                bottom: sheetElement.current.clientHeight,
                threshold: sheetElement.current.clientHeight * 0.2,
            });
        }
    }, [sheetElement]);

    useEffect(() => {
        if (windowSize !== undefined) {
            getSizes();
        }
    }, [windowSize, getSizes]);

    useEffect(() => {
        const elementRef = sheetElement.current;
        if (open) {
            Draggable.create(elementRef, {
                trigger: handleElement.current,
                type: 'y',
                bounds: { minX: 0, minY: 0, maxX: 0, maxY: snap.bottom },
                inertia: true,
                overshootTolerance: 0,
                snap(value) {
                    return value > snap.threshold ? snap.bottom : snap.top;
                },
                onDragStart() {
                    setIsDragging(true);
                },
                onDragEnd() {
                    setIsDragging(false);
                },
                onThrowUpdate() {
                    if (open) {
                        this.y > snap.threshold && closeCallback();
                    }
                },
            });
        }

        return () => {
            if (Draggable.get(elementRef) !== undefined) {
                Draggable.get(elementRef)?.kill();
            }
        };
    }, [sheetElement, handleElement, snap, open, closeCallback]);

    const handleKeyDown = useCallback(
        (e) => {
            if (!open) return;

            // Close modal on ESC
            if (e.key === 'Escape') {
                e.preventDefault();
                closeCallback();
            }

            // Trap focus inside modal
            if (e.key === 'Tab') {
                // if there's only one focusable element just prevent default
                if (focusableChilds?.length === 1) {
                    e.preventDefault();
                }

                // Go to last focusableElement
                if (e.shiftKey && focusableChilds && document.activeElement === focusableChilds[0]) {
                    e.preventDefault();
                    (focusableChilds[focusableChilds.length - 1] as HTMLElement)?.focus();
                }

                // Go to first focusableElement
                if (
                    !e.shiftKey &&
                    focusableChilds &&
                    document.activeElement === focusableChilds[focusableChilds.length - 1]
                ) {
                    e.preventDefault();
                    (focusableChilds[0] as HTMLElement)?.focus();
                }
            }
        },
        [closeCallback, open, focusableChilds],
    );

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    useEffect(() => {
        if (open) {
            // Get focused element before modal opens
            setFocusedElementBeforeOpen(document.activeElement as HTMLElement);

            // Reset Draggable style when reopening
            gsap.set(sheetElement.current, { clearProps: 'all' });
        }

        // Trigger a change in the body's cursor, to make sure mouse cursor in Chrome doesn't stay as
        // a "pointer" until a mousemove happens after clicking an "open modal" button.
        document.body.style.cursor = 'default';
        setTimeout(() => (document.body.style.cursor = ''), 10);
    }, [open, sheetElement]);

    // Get all focusable elements inside modal
    useEffect(() => {
        if (open) {
            const focusableElements =
                'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]';
            setFocusableChilds(containerElement.current.querySelectorAll(focusableElements));
        }
    }, [open, children, containerElement]);

    // Handle focus on open/close
    useEffect(() => {
        if (focusableChilds && open) {
            (focusableChilds[0] as HTMLElement)?.focus(); // if opened, set focus to first focusable child
        } else {
            focusedElementBeforeOpen?.focus(); // if closed, return the focus to last focused element outside modal
        }
    }, [open, focusableChilds, focusedElementBeforeOpen]);

    const classNames = useMemo(() => {
        let classNames = 'yat-modal-bottom-sheet';
        if (variant) classNames += ` yat-modal-bottom-sheet--${variant}`;
        if (open) classNames += ` yat-modal-bottom-sheet--is-open`;
        if (isDragging) classNames += ` yat-modal-bottom-sheet--dragging`;
        if (classes) classNames += ` ${classes}`;

        return classNames;
    }, [variant, open, isDragging, classes]);

    return (
        <div
            role="dialog"
            aria-modal={open ? 'true' : undefined}
            aria-hidden={open ? undefined : 'true'}
            aria-labelledby={modalLabelId}
            className={classNames}
            tabIndex={-1}
        >
            <div ref={containerElement} className="yat-modal-bottom-sheet__sheet-container">
                {backdrop && <YatScrim onClick={closeCallback} />}
                <DepButton
                    variant="secondary"
                    size="small"
                    onClick={closeCallback}
                    classes="yat-modal-bottom-sheet__sr-close-button"
                >
                    Close
                </DepButton>
                <div ref={sheetElement} className="yat-modal-bottom-sheet__sheet">
                    <div ref={handleElement} className="yat-modal-bottom-sheet__handle" />
                    <div
                        className={`yat-modal-bottom-sheet__content${
                            scrollable ? ' yat-modal-bottom-sheet__content--scrollable' : ''
                        }`}
                    >
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
};
